import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { api, ApiError } from "../../api";
import { EFFORT_LABEL } from "../../components/ui";
import { usePickers } from "./shared";

export function StaffNewItem() {
  const [, navigate] = useLocation();
  const pickers = usePickers();
  const [kind, setKind] = useState<"task" | "ticket">("task");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const num = (k: string) => (f.get(k) ? Number(f.get(k)) : null);
    const str = (k: string) => (f.get(k) ? String(f.get(k)) : null);
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ number: number }>("/items", {
        method: "POST",
        json: {
          kind,
          title: str("title"),
          body: str("body") ?? "",
          category: str("category") ?? "other",
          orgId: str("orgId"),
          projectId: str("projectId"),
          assigneeId: str("assigneeId"),
          priority: num("priority"),
          effort: num("effort"),
          dueAt: str("dueAt") ? new Date(`${str("dueAt")}T23:59:00`).toISOString() : null,
        },
      });
      navigate(`/staff/items/${r.number}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create it.");
      setBusy(false);
    }
  };

  return (
    <div className="page-narrow">
      <p className="eyebrow">
        <Link href="/staff/queue">Queue</Link> /
      </p>
      <h1 style={{ marginBottom: 16 }}>New {kind === "task" ? "task" : "ticket"}</h1>
      <form className="card stack" onSubmit={submit}>
        <div className="row" role="radiogroup" aria-label="Type">
          <label className="check"><input type="radio" name="kind" checked={kind === "task"} onChange={() => setKind("task")} /> Internal task</label>
          <label className="check"><input type="radio" name="kind" checked={kind === "ticket"} onChange={() => setKind("ticket")} /> Ticket for a customer</label>
        </div>
        <div className="field">
          <label htmlFor="title">Title</label>
          <input id="title" name="title" type="text" required minLength={3} maxLength={300} />
        </div>
        <div className="field">
          <label htmlFor="body">Description</label>
          <textarea id="body" name="body" maxLength={20000} />
        </div>
        <div className="grid grid-2">
          <div className="field">
            <label htmlFor="orgId">Customer{kind === "ticket" ? "" : " (optional)"}</label>
            <select id="orgId" name="orgId" required={kind === "ticket"}>
              <option value="">{kind === "ticket" ? "Choose…" : "Internal"}</option>
              {pickers.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="projectId">Project</label>
            <select id="projectId" name="projectId">
              <option value="">None</option>
              {pickers.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="priority">Priority</label>
            <select id="priority" name="priority" defaultValue="2">
              <option value="">Not triaged</option>
              <option value="0">P0 Urgent</option>
              <option value="1">P1 High</option>
              <option value="2">P2 Normal</option>
              <option value="3">P3 Low</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="effort">Effort</label>
            <select id="effort" name="effort">
              <option value="">Not estimated</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} · {EFFORT_LABEL[n]}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="assigneeId">Assignee</label>
            <select id="assigneeId" name="assigneeId">
              <option value="">Unassigned</option>
              {pickers.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="dueAt">Due</label>
            <input id="dueAt" name="dueAt" type="date" />
          </div>
        </div>
        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Link href="/staff/queue" className="btn">Cancel</Link>
          <button className="btn btn-primary" disabled={busy}>{busy ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}
